import {useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers'
import {parseResultsSearch} from '../resultsParams'
import {searchByType} from '../../../services/searchService'
import {CommunityDiscussionsItem} from '../../community/components/CommunitySectionItem'
import {CommunityDiscussion} from '../../community/models/CommunityDiscuss'
import apiClient from '../../../helpers/apiClient'

// Maximum forums shown under "Other forums". Enforced by the backend `?limit=…`; the client
// buffer swallows any duplicate that also appears in the recency listing.
const OTHERS_LIMIT = 20
const OTHERS_BUFFER = 5

const asRawList = (data: any): any[] => {
  if (Array.isArray(data)) return data
  return Array.isArray(data?.results) ? data.results : []
}

const fetchForumsByIds = async (ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return []
  const {data} = await apiClient.get('/community/discussions/', {
    params: {post_type: 'forum', status: 'published', ids: ids.join(','), limit: ids.length},
  })
  return asRawList(data)
}

const fetchForumsPreview = async (): Promise<any[]> => {
  const {data} = await apiClient.get('/community/discussions/', {
    params: {
      post_type: 'forum',
      status: 'published',
      limit: OTHERS_LIMIT + OTHERS_BUFFER,
    },
  })
  return asRawList(data)
}

const PER_PAGE = 12

const subheadingStyle: React.CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: 20,
  fontWeight: 500,
  color: '#151515',
  margin: '8px 0 18px',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
}

// Backend delivers snake_case fields; frontend's CommunityDiscussion is camelCase (the DRF
// CamelCaseJSONRenderer usually flips it, but we defend for both here).
const adaptDiscussion = (raw: any): CommunityDiscussion => ({
  id: raw?.id,
  host: !!raw?.host,
  notifications: raw?.notifications ?? 0,
  avatar: raw?.avatar ?? '',
  author: raw?.author ?? '',
  title: raw?.title ?? '',
  tags: Array.isArray(raw?.tags) ? raw.tags : [],
  details: raw?.details ?? '',
  comments: raw?.comments ?? 0,
  members: raw?.members ?? 0,
  memberComments: raw?.memberComments ?? raw?.member_comments ?? 0,
})

const asList = (data: any): any[] => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

const ForumsSearchPage = () => {
  const location = useLocation()
  const history = useHistory()

  const [allForums, setAllForums] = useState<CommunityDiscussion[]>([])
  const [matches, setMatches] = useState<CommunityDiscussion[]>([])
  const [others, setOthers] = useState<CommunityDiscussion[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)

  const {q} = parseResultsSearch(location.search)
  const trimmed = (q ?? '').trim()
  const hasQuery = trimmed.length > 0

  useEffect(() => {
    const container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    return () => container?.classList.remove('member-full-wrapper')
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        // 1. Aggregator ids for matches (same source as the header dropdown).
        let matchIds: string[] = []
        if (trimmed) {
          const res = await searchByType('forums', trimmed, 100, 0)
          matchIds = (res.results ?? [])
            .map((r: any) => String(r?.id ?? ''))
            .filter(Boolean)
        }

        // 2. Hidrate matches (?ids=…) + fetch preview list in parallel.
        const [matchesRaw, previewRaw] = await Promise.all([
          fetchForumsByIds(matchIds),
          fetchForumsPreview(),
        ])

        const matchesById = new Map(
          asRawList(matchesRaw).map(adaptDiscussion).map((f: any) => [String(f.id), f])
        )
        const matched = matchIds
          .map((id: string) => matchesById.get(id))
          .filter((x: any): x is CommunityDiscussion => Boolean(x))

        const full = asRawList(previewRaw).map(adaptDiscussion)
        const matchIdSet = new Set(matched.map((m) => String(m.id)))
        const rest = full
          .filter((f: CommunityDiscussion) => !matchIdSet.has(String(f.id)))
          .slice(0, OTHERS_LIMIT)

        if (cancelled) return
        setAllForums(full)
        setMatches(matched)
        setOthers(rest)
        setPage(1)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed])

  const goToDetail = (id: number) => history.push(`/community/details/${id}`)
  const noop = (_id: number) => {}

  const totalPages = Math.max(1, Math.ceil(others.length / PER_PAGE))
  const paginatedOthers = useMemo(
    () => others.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [others, page]
  )
  const goTo = (p: number) => {
    if (p < 1 || p > totalPages) return
    setPage(p)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const hasMatches = matches.length > 0
  const showResultsHeader = hasQuery
  const showOthersHeader = hasQuery && others.length > 0

  const renderList = (list: CommunityDiscussion[], keyPrefix: string) => (
    <div className='community-list-items'>
      {list.map((f) => (
        <CommunityDiscussionsItem
          key={`${keyPrefix}-${f.id}`}
          id={f.id}
          host={f.host}
          notifications={f.notifications}
          avatar={f.avatar}
          author={f.author}
          title={f.title}
          tags={f.tags}
          details={f.details}
          comments={f.comments}
          members={f.members}
          memberComments={f.memberComments}
          onEnter={goToDetail}
          onEdit={noop}
          onArchive={noop}
        />
      ))}
    </div>
  )

  return (
    <div
      className='members-page members-search-page'
      style={{maxWidth: 1064, width: '100%', margin: '0 auto'}}
    >
      <div className='members-page__header' style={{marginTop: 32}}>
        <h1 className='members-page__title'>
          Forum&nbsp;
          <KTSVG path='/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg' />
        </h1>
      </div>

      {showResultsHeader && (
        <h2 style={subheadingStyle}>Results for &ldquo;{trimmed}&rdquo;</h2>
      )}

      {hasMatches && renderList(matches, 'm')}

      {hasQuery && !hasMatches && !loading && (
        <div style={{padding: '32px 0 8px', color: '#666', fontSize: 16, textAlign: 'center'}}>
          No forums match &ldquo;{trimmed}&rdquo;.
        </div>
      )}

      {(others.length > 0 || (!hasQuery && !loading && allForums.length > 0)) && (
        <>
          {showOthersHeader && <h2 style={{...subheadingStyle, marginTop: 32}}>Other forums</h2>}
          {renderList(paginatedOthers, 'o')}
        </>
      )}

      {totalPages > 1 && (
        <nav className='members-search-pagination' aria-label='Pagination'>
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            aria-label='Previous page'
          >
            ‹
          </button>
          {Array.from({length: totalPages}).map((_, i) => {
            const p = i + 1
            return (
              <button
                key={p}
                type='button'
                className={`members-search-pagination__page ${
                  p === page ? 'members-search-pagination__page--active' : ''
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            )
          })}
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            aria-label='Next page'
          >
            ›
          </button>
        </nav>
      )}
    </div>
  )
}

export {ForumsSearchPage}
